/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useMemo } from 'react';
import { useLocation, useNavigation } from 'react-router';
import { PRODUCT_SEARCH_QUERY_PARAMS } from '@/lib/query-params';

/**
 * Whether the results grid should show its loading treatment for the in-flight navigation.
 *
 * Shared by the two results surfaces (category listing and search), which previously held
 * verbatim copies of this logic. One implementation means the two cannot drift, and a third
 * results surface gets the behaviour by importing rather than by copying.
 *
 * True only for navigations that actually reorder or re-filter the result set:
 *
 * - **Refinements changed** — a different filter selection returns different products.
 * - **Sort changed** — the same products in a different order. This was the gap: the condition
 *   compared refinements only, so re-sorting dimmed the sort dropdown and left the grid looking
 *   untouched until the new results replaced it.
 *
 * Deliberately NOT true for a pagination change. Comparing the whole search string (or
 * `getSearchWithoutClientOnlyParams`) would be shorter and would cover `offset` too, but paging
 * has its own established behaviour and changing it belongs to a pagination ticket rather than
 * arriving as a side effect of a sorting one.
 *
 * Also NOT true for a navigation that leaves the results surface altogether, such as opening a
 * product — see the pathname guard below.
 *
 * Client-only params (the filters-panel flag, pending-action params) never reach here as a
 * difference, because neither `refine` nor `sort` changes when they toggle.
 *
 * @returns `true` while a refinement- or sort-changing navigation is pending
 */
export function useResultsGridLoading(): boolean {
    const location = useLocation();
    const navigation = useNavigation();

    return useMemo(() => {
        if (navigation.state === 'idle' || !navigation.location) {
            return false;
        }

        // A destination outside the results surface carries neither param, so without this both
        // comparisons below read as "changed" and the grid being left behind fills with skeletons
        // for the length of the trip (MR !44 review, decision 2026-08-11).
        if (navigation.location.pathname !== location.pathname) {
            return false;
        }

        const currentParams = new URLSearchParams(location.search);
        const nextParams = new URLSearchParams(navigation.location.search);

        const currentRefines = currentParams.getAll(PRODUCT_SEARCH_QUERY_PARAMS.REFINE);
        const nextRefines = nextParams.getAll(PRODUCT_SEARCH_QUERY_PARAMS.REFINE);
        const refinementsChanged =
            currentRefines.length !== nextRefines.length ||
            currentRefines.some((currentRefine, index) => currentRefine !== nextRefines[index]);

        const sortChanged =
            (currentParams.get(PRODUCT_SEARCH_QUERY_PARAMS.SORT) ?? '') !==
            (nextParams.get(PRODUCT_SEARCH_QUERY_PARAMS.SORT) ?? '');

        return refinementsChanged || sortChanged;
    }, [location.pathname, location.search, navigation.location, navigation.state]);
}
